import { YesNoUnsure } from '@bybeam/platform-types';
import { TemporaryDocument } from '@platform-ui/@types/document.js';
import { DocumentFeedback } from '@platform-ui/common/components/DocumentUpload/types.js';
import Button from '../Button/index.js';
import {
  DISPLAY_PREDICTION_THRESHOLD,
  getDisplayLabel,
  isExistingDocument,
} from '../DocumentUpload/utils.js';
import Icon from '../Icon/index.js';
import Tooltip from '../Tooltip/index.js';
import Typography from '../Typography/index.js';

interface DocumentFeedbackLabelProps {
  document: TemporaryDocument;
  feedback: DocumentFeedback;
}

export default function DocumentFeedbackLabel({
  document,
  feedback,
}: DocumentFeedbackLabelProps): JSX.Element {
  if (!isExistingDocument(document)) return null;

  const hasPrediction = () =>
    !!document.summary?.prediction?.label && !!document.summary?.prediction.probability;

  if (!hasPrediction()) return null;

  const isClassified = () => document.summary.prediction.probability > DISPLAY_PREDICTION_THRESHOLD;

  const documentFeedback = document.summary?.prediction?.feedback?.[0];
  const documentLabel = document.summary?.prediction?.label;

  const positiveFeedback = (): boolean => {
    if (documentFeedback) if (documentFeedback.accurate === YesNoUnsure.Yes) return true;
    return false;
  };

  const negativeFeedback = (): boolean => {
    if (documentFeedback) if (documentFeedback.accurate === YesNoUnsure.No) return true;
    return false;
  };

  const tooltipLabel = (displayLabel: string): string => {
    return `Thanks for your feedback. You indicated that ${displayLabel} is the correct document type`;
  };

  const getFeedback = (feedbackType?: YesNoUnsure) =>
    feedback.get(document.id, feedbackType, isClassified());

  return (
    <div className="flex gap-2 mb-1 justify-between">
      {isClassified() ? (
        <div className="flex gap-2 flex-row">
          <Tooltip title={'This document category was suggested by AI'}>
            <Icon size="MD" type="aiEnabled" className="pt-1" />
          </Tooltip>
          <Typography variant="body" textTransform="capitalize" bold>
            {getDisplayLabel(document.summary.prediction.label)}
          </Typography>
        </div>
      ) : (
        <Typography variant="body" textTransform="capitalize" bold>
          Unclassified
        </Typography>
      )}
      <div className="flex gap-2 flex-row mr-10">
        <Button
          variant="text"
          onClick={() => getFeedback(YesNoUnsure.Unsure)}
          data-testid="giveFeedback"
        >
          <Typography variant="label" bold={true}>
            Give Feedback
          </Typography>
        </Button>
        {positiveFeedback() ? (
          <Tooltip title={tooltipLabel(isClassified() ? getDisplayLabel(documentLabel) : '')}>
            <Button
              variant="text"
              icon="thumbUp"
              disabled={!isClassified()}
              onClick={() => getFeedback(YesNoUnsure.Yes)}
              data-testid="positiveFeedback"
            />
          </Tooltip>
        ) : (
          <Button
            variant="text"
            icon="thumbUpOutline"
            disabled={!isClassified()}
            onClick={() => getFeedback(YesNoUnsure.Yes)}
            data-testid="submitPositiveFeedback"
          />
        )}
        {negativeFeedback() ? (
          <Tooltip title={tooltipLabel(getDisplayLabel(documentFeedback.preferredLabel))}>
            <Button
              variant="text"
              icon="thumbDown"
              onClick={() => getFeedback(YesNoUnsure.No)}
              data-testid="negativeFeedback"
            />
          </Tooltip>
        ) : (
          <Button
            variant="text"
            icon="thumbDownOutline"
            onClick={() => getFeedback(YesNoUnsure.No)}
            data-testid="submitNegativeFeedback"
          />
        )}
      </div>
    </div>
  );
}
